import React, { memo, useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react'
import shallow from 'zustand/shallow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { calculateWidth, gridUnit, maxLeftBounds, maxTopBounds } from '../../constants/gridConfig'
import { useStore } from '../../store/store';
import { useDrop } from 'react-dnd';
import { ItemTypes, MASKS_TYPE, antdFormError, borderColumnColor, BOX_CLICK_SELECTOR, containerBackground, defaultItemValues, pageBackground, viewerTypes } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import { initializeBoxContent, isADefaultBox, isADrawBox, isAOneRowBox, isASelectionBox, isATableBox, isATitleBox, reinitializeBoxWidthAndHeight } from '../../utils/boxes';
import BorderColumn from './BorderColumn';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { App, Button } from 'antd';
import Tooltip from '../common/Tooltip';
import AdderMenu from '../Container/AdderMenu';
import { useResizeHandleClearance } from '../common/useResizeHandleClearance';
import { useOutsideAlerter } from '../../hooks';
import { CustomDragLayer } from '../common/CustomDragLayer';
import { columnStackAlignItems, computeColumnWidth, containerColumnGap, getTopLevelContainer } from '../../utils/columns';
import Column from './Column';
import { getColumnById, getContainerByColumnId, getMaskById, getBoxesByColumnId } from '../../store/lookups';
import { resolveSnapOffset } from '../../snapping/snapSession';
import { acceptsInPageBackground, draggedBoxType, isPageBackgroundId } from '../../utils/pageBackground';

const selector = (id, fromViewer) => ({
  configuration,
  columns,
  maskEditing,
  masks,
  moveMaskBox,
  moveBoxInfinite,
  dropBoxInColumn,
  landscape,
  fromPdf,
  containers,
  rowContainers,
  currentProposal,
  selectedContainerIds,
  selectedContainer,
  zoomLevel,
  focusLayoutColumn,
  removeColumnFromRow,
  clearColumn,
  addRowToColumn,
}) => {
  const column = getColumnById(columns, id);
  // Sous-colonne d'une ligne imbriquée : son "container" est la ligne, qui
  // porte les mêmes champs (editable, columnsIds) que le container top-level.
  const container =
    getContainerByColumnId(containers, id) ??
    getContainerByColumnId(rowContainers, id);
  const mask = !column && (fromViewer || fromPdf || (maskEditing.length > 0
    && maskEditing.includes(id)))
    && getMaskById(masks, id);
  return {
    fromPdf,
    marginLeft: configuration.margins.left * gridUnit,
    marginRight: configuration.margins.right * gridUnit,
    column,
    configuration,
    mask,
    moveBox: mask ? moveMaskBox : moveBoxInfinite,
    dropBoxInColumn,
    landscape,
    container,
    // Écart réglé sur le container top-level, que ses lignes imbriquées
    // reprennent : la largeur calculée ici doit suivre celui du rendu.
    columnGap: column
      ? containerColumnGap(getTopLevelContainer({ columnId: id, containers, rowContainers }))
      : undefined,
    currentProposal,
    isContainerSelected:
      !!container &&
      (selectedContainerIds?.includes(container.id) ||
        selectedContainer?.id === container.id),
    zoomLevel,
    focusLayoutColumn,
    removeColumnFromRow,
    clearColumn,
    addRowToColumn,
  }
};

const storeBoxesSelector = (state) => state.boxes;
const headerSelector = ({ masks }) => getMaskById(masks, MASKS_TYPE.HEADER.id);
const footerSelector = ({ masks }) => getMaskById(masks, MASKS_TYPE.FOOTER.id);

const accept = [
  'newBox',
  ItemTypes.CONTAINER_COPIED,
  ItemTypes.IMG,
  ItemTypes.TEXT,
  ItemTypes.SHAPE,
  ItemTypes.LINE,
  ItemTypes.TABLE,
  ItemTypes.TITLE,
  ItemTypes.TITLE_2,
  ItemTypes.TITLE_3,
  ItemTypes.TITLE_4,
  ItemTypes.SUMMARY,
  ItemTypes.CHECKBOXS_VARIABLE,
  ItemTypes.SELECTOR_VARIABLE,
  ItemTypes.TEXT_VARIABLE,
  ItemTypes.IMG_VARIABLE,
  ItemTypes.TABLE_VARIABLE,
  ItemTypes.GROUP_BOXES,
  ItemTypes.ORG_CHART,
  ItemTypes.ACCORDION_PANEL,
  ItemTypes.INFO_CARD
];

// Types pilotant la structure du document (sommaire, plan, sections) : refusés
// dans une sous-colonne de ligne imbriquée, cf. boxCanDrop.
const STRUCTURAL_BOX_TYPES = [
  ItemTypes.TITLE,
  ItemTypes.TITLE_2,
  ItemTypes.TITLE_3,
  ItemTypes.TITLE_4,
  ItemTypes.SUMMARY,
  ItemTypes.SECTION,
  ItemTypes.SECTION_LINK,
  ItemTypes.RECURSIVE_SECTION,
  ItemTypes.RECURSIVE_MODEL,
];

// Décalage du crayon (à gauche) et du « + » d'ajout de ligne (à droite) quand
// ils se chevauchent : 6px d'écart entre le crayon (24px) et le « + » (20px).
const SIDE_BY_SIDE_OFFSET = 14;
// Hauteur de cellule sous laquelle ils se chevauchent : le crayon centré
// descend à h/2 + 12, le « + » (20px, centré dans la bande de 14px du bas)
// monte à h - 17.
const ADDERS_OVERLAP_HEIGHT = 58;

const ColumnWrapper = memo(({
  id,
  isContainerHovered,
  containerColumnsLength = 1,
  setIsResizingContainer,
  fromViewer,
  isLastColumn,
  isFirstColumn,
  isMiddleColumn,
  isNested = false,
  parentWidthPx,
  showCellOutline = false,
  // Sous-colonne d'une ligne qui en compte plusieurs, retirable seule (cf.
  // ColumnsRow).
  canRemoveFromRow = false,
}) => {
  const header = useStore(headerSelector)
  const footer = useStore(footerSelector)
  const {
    fromPdf,
    configuration,
    column,
    mask,
    marginLeft,
    marginRight,
    moveBox,
    dropBoxInColumn,
    landscape,
    container,
    columnGap,
    currentProposal,
    isContainerSelected,
    zoomLevel,
    focusLayoutColumn,
    removeColumnFromRow,
    clearColumn,
    addRowToColumn,
  } = useStore(selector(id, fromViewer), shallow);
  const unlockProposal = useStore(({ configuration }) => configuration.unlockProposal)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  // Cellule désignée par un clic (ici ou dans les réglages avancés du panel de
  // droite) : éclairée tant qu'on ne clique pas ailleurs.
  const isLayoutFocused = useStore(({ layoutFocus }) => layoutFocus?.columnId === id)

  const { modal: modalApi } = App.useApp();
  const confirmReplaceBox = useCallback(
    (replaceBox, setBoxIdToReplaceFn) => {
      modalApi.confirm({
        title: "Remplacer l'élément existant par celui-ci ?",
        icon: <ExclamationCircleFilled />,
        cancelText: "Annuler",
        okText: "Remplacer",
        // Un dépôt sur un élément existant est vite fait par erreur : Entrée
        // doit annuler, pas écraser.
        autoFocusButton: 'cancel',
        onOk() {
          replaceBox();
          setBoxIdToReplaceFn();
        },
        onCancel() {
          setBoxIdToReplaceFn();
        },
      });
    },
    [modalApi]
  );

  const storeBoxes = useStore(storeBoxesSelector);
  const boxes = useMemo(() => {
    if (mask) return mask.boxes;
    return getBoxesByColumnId(storeBoxes, id);
  }, [mask, storeBoxes, id]);

  const titles = useMemo(() => boxes.filter((box) => isATitleBox(box.type)), [boxes]);

  const [boxIdToReplace, setBoxIdToReplace] = useState();
  const [controlCol, setControlCol] = useState(false);
  const [controlActionHovered, setControlActionHovered] = useState(false);
  const [controlActionClicked, setControlActionClicked] = useState(false);
  const [deleteButtonHovered, setDeleteButtonHovered] = useState(false);
  // mouseEnter/Leave (et non mouseOver) : pas de bouillonnement, seule la
  // cellule réellement survolée s'allume, pas ses parents.
  const [isCellHovered, setIsCellHovered] = useState(false);
  const [isRemoveHovered, setIsRemoveHovered] = useState(false);
  const [isRowAdderHovered, setIsRowAdderHovered] = useState(false);

  const size = mask ? 1 : (column?.size ?? 1);
  const columnWidthPercentage = 100 * size;
  const parentIdString = column ? `column-${column.id}` : mask ? `mask-${mask.id}` : null;
  const drawMode = !!(mask || column?.drawMode);
  const columnWidth = computeColumnWidth({
    landscape,
    drawMode,
    // Les marges de page ne s'appliquent qu'au niveau top-level : une
    // sous-colonne se partage la largeur déjà démarginée de sa colonne parente.
    marginLeft: isNested ? 0 : marginLeft,
    marginRight: isNested ? 0 : marginRight,
    containerColumnsLength,
    size,
    parentWidthPx,
    columnGap,
  })

  const reinitializeBoxLeftAndTop = (box, { clientX, clientY }, zoomFactor) => {
    const bounds = document.getElementById(parentIdString).getBoundingClientRect();
    let left = (clientX - bounds.left - box.width / (2 / zoomFactor)) / zoomFactor;
    const defaultTop = clientY - bounds.top;
    let top = box.height
      ? (defaultTop - (box.height === 'auto' ? 0 : Number(box.height / (2 / zoomFactor)))) / zoomFactor
      : defaultTop / zoomFactor;

    if (box.left) {
      left = box.left;
    }
    if (isATableBox(box.type)) {
      left = configuration.margins.left * gridUnit
      box.width = calculateWidth(configuration.margins, 0, 0);
    } else if (box.type === ItemTypes.SUMMARY) {
      left = configuration.margins.left * gridUnit;
    }

    return {
      ...box,
      left: left < maxLeftBounds ? Math.round(maxLeftBounds) : Math.round(left),
      top: top < maxTopBounds ? Math.round(maxTopBounds) : Math.round(top),
    };
  }

  const handleBoxDropped = ({
    newBox,
    boxDrawMode,
    updateContainerHeight,
    boxIdToReplace }) => {
    dropBoxInColumn({
      id,
      newBox,
      drawMode: boxDrawMode,
      maskId: mask ? mask.id : undefined,
      updateContainerHeight,
      boxIdToReplace
    })
  }

  const createNewBox = (box, { clientX, clientY }) => {
    const boxBase = initializeBoxContent({
      box,
      configuration,
      titles,
      mask,
      column,
      parentId: id
    });
    if (drawMode) {
      const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;

      const newBox = reinitializeBoxLeftAndTop(boxBase, { clientX, clientY }, zoomFactor);
      handleBoxDropped({ newBox, boxDrawMode: true })
    } else {
      const newBox = reinitializeBoxWidthAndHeight({ box: boxBase, columnWidth, onCreate: true, header, footer });
      const boxDrawMode = isADrawBox(box.type);
      if (boxes.length === 0) {
        handleBoxDropped({
          newBox,
          boxDrawMode,
          updateContainerHeight: boxDrawMode,
        })
      }
      else {
        setBoxIdToReplace(boxes[0].id)
        confirmReplaceBox(() => {
          handleBoxDropped({
            newBox,
            boxDrawMode,
            updateContainerHeight: boxDrawMode,
            boxIdToReplace: boxes[0].id
          })
        },
          setBoxIdToReplace)
      }
    }
  }

  const onDrop = ({ box, monitor }) => {
    if (!monitor.isOver({ shallow: true })) return;

    if (monitor.getItemType() === 'newBox') {
      const clientOffset = monitor.getClientOffset();
      createNewBox(box, { clientX: clientOffset.x, clientY: clientOffset.y })
    }
    else {
      if (drawMode) {
        const delta = monitor.getDifferenceFromInitialOffset();
        const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;
        // Même fonction pure que l'aperçu de drag : la box se pose exactement
        // là où les guides l'annonçaient. Sans session ouverte (magnétisme
        // désactivé, drag hors mode dessin), l'offset ressort inchangé.
        const offset = resolveSnapOffset({ x: delta.x / zoomFactor, y: delta.y / zoomFactor });

        let left = Math.round(box.left + offset.x);
        let top = Math.round(box.top + offset.y);
        moveBox(left, top, box.id, id);
      }
    }
  }

  const boxCanDrop = useCallback((_, monitor) => {
    const box = monitor.getItem();
    if (box.type === ItemTypes.GROUP_BOXES && !drawMode) return false;
    // Le décor de page ne décore la page qu'avec des traits, formes et images.
    const draggedType = draggedBoxType(monitor.getItemType(), box);
    if (isPageBackgroundId(id) && !acceptsInPageBackground(draggedType)) return false;
    // Sommaire et navigation par titres sont indexés sur les containers
    // top-level : dans une ligne imbriquée ces box seraient invisibles au plan
    // du document.
    if (isNested && STRUCTURAL_BOX_TYPES.includes(box.type)) return false;
    if (container?.editable === false && currentProposal.id !== null) return false;
    if (currentProposal.id === null && container?.from_linked_section) return false;
    // Can't move box from parent to another one
    if (box.parentId && box.parentId !== id) {
      return false
    }
    // If column less than full size and dragged box require full size
    if (!drawMode && isAOneRowBox(box.type) && size !== 1) {
      return false;
    }
    // If column is in drawMode and dragged box is a drawMode or is only for default columns
    if ((isADrawBox(box.type) || isADefaultBox(box.type)) && drawMode) {
      return false
    }
    // If dropped box is a mask
    if (boxes.length === 1 && boxes[0].type === ItemTypes.MASK) {
      return false
    }
    if (boxes.length === 1 && isASelectionBox(boxes[0].type)) {
      return false
    }
    if (!isTemplate && !unlockProposal) {
      return false
    }
    return true
  }, [container?.editable, currentProposal.id, id, drawMode, size, boxes, isTemplate, unlockProposal, container?.from_linked_section, isNested])

  const dropCommonProps = useMemo(() => ({
    accept,
    collect: (monitor) => ({
      boxIsOver: monitor.isOver({ shallow: true }) && (!drawMode || monitor.getItemType() === 'newBox'),
      canDrop: monitor.canDrop(),
      isDragging: monitor.getItem() !== null && monitor.getItemType() === 'newBox'
    }),
  }), [drawMode])

  const [{ boxIsOver, canDrop, isDragging }, dropColumn] = useDrop({
    ...dropCommonProps,
    canDrop: boxCanDrop,
    drop: (box, monitor) => onDrop({ box, monitor }),
  });

  const willBoxBeReplaced = (boxIdToReplace && boxes.length === 1 && boxes[0].id === boxIdToReplace)

  // Réinitialiser la sélection de colonne lorsque le container n'est plus sélectionné
  useEffect(() => {
    if (!isContainerSelected && controlCol) {
      setControlCol(false);
    }
  }, [isContainerSelected, controlCol]);

  const boxPosition = useCallback(() => {
    if (boxes.length !== 1) {
      // Plusieurs box dans la cellule : leur pile se place en bloc, selon le
      // réglage de la cellule (centré par défaut, comme avant). L'alignement
      // vertical de chaque box n'a pas de sens ici — c'est la pile entière qui
      // se déplace, et « réparti » demande en plus toute la hauteur (le
      // justifyContent se règle alors sur la pile, cf. Column.jsx).
      return {
        justifyContent: "center",
        alignItems: columnStackAlignItems(column?.verticalAlign),
      }
    }
    const box = boxes.find((box) => boxes[0].id === box.id);
    return {
      justifyContent: box.type !== ItemTypes.LINE_BREAK ? box.positions?.horizontal : "flex-start",
      alignItems: box.positions?.vertical
    }

  }, [boxes, column?.verticalAlign])

  const addTextBoxOnDoubleClick =
    (e) => {
      const selectedBoxId = useStore.getState().selectedBoxId;
      // Pas de texte dans le décor de page (cf. boxCanDrop).
      if (isPageBackgroundId(id)) return;
      if (!(isTemplate && !unlockProposal) || fromViewer === viewerTypes.PAGENUMBER || fromViewer === viewerTypes.DMZ || fromViewer === viewerTypes.PREVIEW) return;
      if ((boxes.length > 0 && (
        !drawMode ||
        boxes[0].type === ItemTypes.MASK ||
        boxes[0].type === ItemTypes.SECTION ||
        boxes[0].type === ItemTypes.RECURSIVE_SECTION ||
        boxes[0].type === ItemTypes.RECURSIVE_MODEL ||
        boxes.some((b) => selectedBoxId && b.id === selectedBoxId)))
        || (!container?.editable &&
          container?.hasOwnProperty("editable") &&
          currentProposal.id !== null)) return;

      const { clientX, clientY } = e;
      const newBox = {
        ...defaultItemValues.text,
        type: 'text',
      };
      createNewBox(newBox, { clientX, clientY })
    };

  // Cellule vide et remplissable : elle propose alors un bouton au survol qui
  // ouvre l'AdderMenu, plutôt que d'obliger à glisser une vignette depuis le
  // panel de gauche. Mêmes verrous que le drop (cf. boxCanDrop), la sélection
  // du type d'élément étant ensuite filtrée par l'AdderMenu lui-même.
  // Le mode dessin est exclu : une box y est posée à des coordonnées libres,
  // il n'y a pas d'emplacement unique où un bouton centré aurait un sens.
  const canFillCell =
    !drawMode &&
    !mask &&
    !fromPdf &&
    boxes.length === 0 &&
    // Une colonne découpée en lignes ne porte pas de box en direct : elle est
    // vide au sens du modèle, mais pleine à l'écran. Ce sont ses sous-colonnes
    // qui proposent le bouton, pas elle.
    !column?.rowContainerIds?.length &&
    (isTemplate || unlockProposal) &&
    !(container?.editable === false && currentProposal.id !== null) &&
    !(currentProposal.id === null && container?.from_linked_section) &&
    // Aucun viewer (aperçu, dmz, numéro de page, formulaire de variables) : on
    // n'y compose pas le document, et c'est la même limite que handleCellClick.
    !fromViewer

  const [cellAdderAnchor, setCellAdderAnchor] = useState(null);
  const cellRef = useRef(null);
  // Compose la ref de drop de react-dnd : on a besoin du noeud pour fermer le
  // menu au clic en dehors.
  const setCellNode = useCallback((node) => {
    cellRef.current = node;
    dropColumn(node);
  }, [dropColumn]);
  const closeCellAdder = useCallback(() => setCellAdderAnchor(null), []);
  useOutsideAlerter(cellRef, closeCellAdder, !!cellAdderAnchor);

  // Le menu s'ancre sur la colonne de premier niveau qui héberge la cellule, et
  // non sur la cellule : il s'ouvre alors sous cette colonne, comme le menu du
  // « + » d'ajout de colonne, au lieu de sauter d'une ligne à l'autre. Pour une
  // colonne simple, l'ancre est la cellule elle-même.
  const openCellAdder = useCallback((event) => {
    event.stopPropagation();
    let anchor = cellRef.current;
    let parentColumn = anchor?.parentElement?.closest('[id^="column-"]');
    while (parentColumn) {
      anchor = parentColumn;
      parentColumn = anchor.parentElement?.closest('[id^="column-"]');
    }
    setCellAdderAnchor(anchor ? { current: anchor } : null);
  }, []);

  // Cliquer une cellule désigne la structure, pas son contenu : le panel de
  // droite s'ouvre sur le réglage de cette cellule. Un clic sur la box elle-même
  // reste un clic de sélection de box — c'est la même lecture que dans
  // Container.jsx, d'où le sélecteur partagé.
  const handleCellClick = (event) => {
    if (mask || fromPdf || fromViewer) return;
    if (event.target?.closest?.(BOX_CLICK_SELECTOR)) return;
    // Les cellules s'imbriquent (colonne > ligne > sous-colonne) et l'événement
    // remonte : sans ce test, le handler de la colonne parente s'exécuterait
    // après celui de la sous-colonne cliquée et écraserait le focus par le sien.
    // On ne garde que la cellule la plus profonde qui contient la cible.
    if (event.target?.closest?.('[id^="column-"]') !== event.currentTarget) return;
    focusLayoutColumn(id);
  };

  const handleCellPick = (box) => {
    // clientX/clientY ne servent qu'en mode dessin, exclu ici : le centre de la
    // cellule est une valeur de repli sans effet.
    const rect = cellRef.current?.getBoundingClientRect();
    createNewBox(box, {
      clientX: rect ? rect.left + rect.width / 2 : 0,
      clientY: rect ? rect.top + rect.height / 2 : 0,
    });
  };

  // Révèle la structure d'une colonne découpée en lignes : sans ça, une cellule
  // vide est indiscernable du vide autour. Édition seulement — jamais dans
  // l'aperçu ni le PDF.
  const showCellChrome =
    isNested &&
    showCellOutline &&
    !fromPdf &&
    fromViewer !== viewerTypes.PREVIEW &&
    fromViewer !== viewerTypes.DMZ

  // Titres, sommaire, sections, sélections et masques : interdits dans une
  // ligne (cf. boxCanDrop), et une section ne se vide pas en retirant sa seule
  // box (ses containers injectés resteraient orphelins).
  const holdsStructuralBox = boxes.some((b) =>
    STRUCTURAL_BOX_TYPES.includes(b.type) ||
    isASelectionBox(b.type) ||
    b.type === ItemTypes.MASK
  )

  // Colonne de container sélectionné qui porte du contenu : au survol, elle
  // s'éclaire et propose un « × » qui la vide, comme le « × » d'une ligne.
  // Une colonne découpée en lignes est exclue : chaque ligne a déjà le sien.
  const canClearColumn =
    !isNested &&
    isContainerSelected &&
    !drawMode &&
    !mask &&
    !fromPdf &&
    !fromViewer &&
    (isTemplate || unlockProposal) &&
    boxes.length > 0 &&
    !holdsStructuralBox &&
    !column?.rowContainerIds?.length &&
    !container?.from_linked_section &&
    !(container?.editable === false && currentProposal.id !== null)

  // Même « + » que la bande d'ajout de ligne (cf. ColumnsRow), sous une colonne
  // simple de container multi-colonnes : il la découpe en deux lignes sans
  // passer par le panel de droite. Container à une colonne exclu : on y ajoute
  // plutôt un container en dessous.
  const canAddRowBelow =
    !isNested &&
    isContainerSelected &&
    containerColumnsLength > 1 &&
    !drawMode &&
    !mask &&
    !fromPdf &&
    !fromViewer &&
    (isTemplate || unlockProposal) &&
    !column?.rowContainerIds?.length &&
    !container?.from_linked_section &&
    !(container?.editable === false && currentProposal.id !== null) &&
    !holdsStructuralBox

  const showRowAdder = canAddRowBelow && isCellHovered && !isDragging
  const rowAdderRef = useRef(null)
  // Cellule vide basse (container d'une ligne) : le crayon (centré) et le « + »
  // de la bande (centré en bas) tombent au même endroit. Ils se mettent alors
  // côte à côte, crayon à gauche, « + » à droite ; dans une cellule plus haute,
  // chacun reste centré. Hauteur hors zoom (offsetHeight), comme les tailles
  // des boutons.
  const [cellAddersSideBySide, setCellAddersSideBySide] = useState(false)
  useLayoutEffect(() => {
    const height = cellRef.current?.offsetHeight ?? 0
    setCellAddersSideBySide(canFillCell && showRowAdder && height < ADDERS_OVERLAP_HEIGHT)
  }, [canFillCell, showRowAdder])
  const rowAdderClearance = useResizeHandleClearance({
    bandRef: rowAdderRef,
    active: showRowAdder,
    plusSize: 20,
  })

  const columnStyle = useMemo(() => {
    const baseStyle = {
      width: `${columnWidthPercentage}%`,
      maxWidth: `${columnWidthPercentage}%`,
      display: "flex",
      position: 'relative',
      ...(showCellChrome
        ? {
          outline: `1px dashed ${borderColumnColor}`,
          outlineOffset: -1,
          backgroundColor: isCellHovered ? 'rgba(64, 150, 255, 0.08)' : undefined,
          transition: 'background-color 150ms ease',
        }
        : {}),
      ...(canClearColumn
        ? {
          backgroundColor: isCellHovered ? 'rgba(64, 150, 255, 0.08)' : undefined,
          transition: 'background-color 150ms ease',
        }
        : {}),
    }
    if (drawMode) return baseStyle
    return { ...baseStyle, ...boxPosition() }
  }, [boxPosition, columnWidthPercentage, drawMode, showCellChrome, isCellHovered, canClearColumn])

  const borderColumnProps = useMemo(() => ({
    willBoxBeReplaced,
    containerColumnsLength,
    columnId: id,
    dropCommonProps,
    isContainerHovered: isContainerHovered && !fromPdf,
    boxCanDropInColumn: canDrop,
    hasAOneRowBox: boxes.length === 1 && isAOneRowBox(boxes[0].type)
  }), [containerColumnsLength, dropCommonProps, id, isContainerHovered, willBoxBeReplaced, canDrop, boxes, fromPdf])

  if (!column && !mask) return null;

  // Survol suivi dès qu'on édite, et non seulement quand une action de survol
  // est possible : ces actions dépendent de la sélection du container. Suivi
  // conditionnel, un container sélectionné par un clic sur sa box n'allumait
  // rien tant que la souris ne ressortait pas (le mouseenter avait déjà eu
  // lieu), et une cellule quittée pendant que le suivi était coupé restait
  // « survolée » : ses boutons réapparaissaient à la sélection suivante.
  const tracksHover = !fromPdf && !mask
  // Un seul « × » par cellule : retrait d'une sous-colonne, ou vidage d'une
  // colonne de container.
  const showCellRemove = (canRemoveFromRow || canClearColumn) && isCellHovered && !isDragging

  return <div
    ref={setCellNode}
    id={parentIdString}
    style={columnStyle}
    data-role={pageBackground}
    data-parent={containerBackground}
    onClick={handleCellClick}
    onDoubleClick={addTextBoxOnDoubleClick}
    onMouseEnter={tracksHover ? () => setIsCellHovered(true) : undefined}
    onMouseLeave={tracksHover ? () => {
      setIsCellHovered(false)
      setIsRemoveHovered(false)
      setIsRowAdderHovered(false)
    } : undefined}
  >

    {/* Pas de bordures interactives sur une sous-colonne : leurs actions
        (ajout/suppression de colonne) passent par la sélection de container
        top-level. La disposition d'une ligne se change via son picker. */}
    {!drawMode && !isNested && fromViewer !== viewerTypes.PREVIEW && <BorderColumn
      {...borderColumnProps}
      position='left'
      fromViewer={fromViewer}
    />}
    <Column
      id={id}
      drawMode={drawMode}
      canDrop={canDrop}
      isDragging={isDragging}
      columnWidth={columnWidth}
      configuration={configuration}
      boxes={boxes}
      mask={mask}
      fromViewer={fromViewer}
      linkedMaskHasNotPageNumber={column?.linkedMaskHasNotPageNumber}
      columnMaskId={column?.maskId}
      rowContainerIds={column?.rowContainerIds}
      verticalAlign={column?.verticalAlign}
      isResizable={
        fromViewer !== viewerTypes.PAGENUMBER &&
        fromViewer !== viewerTypes.DMZ &&
        fromViewer !== viewerTypes.PREVIEW &&
        !landscape &&
        drawMode &&
        (!mask || mask.id === MASKS_TYPE.HEADER.id || mask.id === MASKS_TYPE.FOOTER.id) &&
        !column?.maskId &&
        !column?.isOldPage &&
        !boxes.some((b) => isASelectionBox(b.type) || b.type === ItemTypes.ORG_CHART)
      }
      backgroundColor={mask ? mask?.backgroundColor : column?.backgroundColor}
      setIsResizingContainer={setIsResizingContainer}
    />
    {!drawMode && !isNested && fromViewer !== viewerTypes.PREVIEW && <BorderColumn
      fromViewer={fromViewer}
      {...borderColumnProps}
      position='right'
      setControlCol={setControlCol}
      controlCol={controlCol}
      setControlActionClicked={setControlActionClicked}
      setControlActionHovered={setControlActionHovered}
      controlActionClicked={controlActionClicked}
      setDeleteButtonHovered={setDeleteButtonHovered}
      deleteButtonHovered={deleteButtonHovered}
      controlActionHovered={controlActionHovered}
      isLastColumn={isLastColumn}
      isFirstColumn={isFirstColumn}
      isMiddleColumn={isMiddleColumn}
    />}
    <div
      style={{
        position: 'absolute',
        backgroundColor: deleteButtonHovered ? "red" : borderColumnColor,
        opacity: 0.3,
        width: '100%',
        height: '100%',
        zIndex: 2,
        scale: `${(boxIsOver && canDrop) || controlCol === column?.id ? '1' : '0'} 1`,
        left: 0,
        transformOrigin: 'left'
      }}
    />
    {/* Pas en mode dessin : la cellule y est la zone où l'on dessine, l'éclairer
        couvrirait tout le dessin, et aucun panel de disposition ne l'éteindrait. */}
    {isLayoutFocused && !drawMode && !mask && !fromPdf && !fromViewer && <div
      aria-hidden='true'
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        pointerEvents: 'none',
        // Même teinte que le bloc éclairé dans le panel (cf. .layout-focus-highlight).
        backgroundColor: 'rgba(64, 150, 255, 0.22)',
        boxShadow: 'inset 0 0 0 1px rgba(64, 150, 255, 0.55)',
      }}
    />}
    {/* Masqué pendant un drag : la cellule est déjà une cible de drop, un bouton
        au milieu de l'aperçu de dépôt ne ferait que brouiller la lecture. */}
    {canFillCell && !isDragging && (isCellHovered || cellAdderAnchor) && <Tooltip
      title='Ajouter un élément'
      placement='top'
      mouseEnterDelay={0.3}
      // Au-dessus de l'AdderMenu (10001), sinon l'infobulle passe derrière lui.
      zIndex={10050}
    >
      {/* Crayon, pas un « + » : la bande d'ajout de ligne (ColumnsRow) en porte
          déjà un juste en dessous. Icône seule pour tenir dans une colonne étroite. */}
      <Button
        shape='circle'
        size='small'
        data-outside-ignore='true'
        aria-label='Ajouter un élément'
        icon={<FontAwesomeIcon icon={icon({ name: 'pen', style: 'light' })} />}
        onClick={openCellAdder}
        // Le double-clic de la cellule insère un bloc texte : l'arrêter ici
        // évite qu'un double-clic sur le bouton fasse les deux.
        onDoubleClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: cellAddersSideBySide
            ? `translate(calc(-50% - ${SIDE_BY_SIDE_OFFSET}px), -50%)`
            : 'translate(-50%, -50%)',
          // Au-dessus du voile de survol/drop de la cellule (zIndex 2).
          zIndex: 3,
        }}
      />
    </Tooltip>}
    {/* Cellule concernée en rouge au survol du « × », comme la ligne (cf.
        ColumnsRow). */}
    {showCellRemove && isRemoveHovered && <div
      aria-hidden='true'
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        pointerEvents: 'none',
        border: `1px solid ${antdFormError}`,
        backgroundColor: 'rgba(255, 77, 79, 0.3)',
      }}
    />}
    {/* Sous-colonne : en haut à gauche, le « × » de la ligne occupe déjà le
        coin haut droit (cf. ColumnsRow). Colonne de container : coin haut
        droit, comme ce « × » de ligne. */}
    {showCellRemove && <Button
      title={canRemoveFromRow ? 'Supprimer la colonne' : 'Supprimer le contenu'}
      shape='circle'
      data-outside-ignore='true'
      onMouseEnter={() => setIsRemoveHovered(true)}
      onMouseLeave={() => setIsRemoveHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        setIsRemoveHovered(false);
        // Pour une sous-colonne, `container` est sa ligne (cf. selector).
        if (canRemoveFromRow) {
          removeColumnFromRow({ rowContainerId: container.id, columnId: id });
        } else {
          clearColumn(id);
        }
      }}
      onDoubleClick={(e) => e.stopPropagation()}
      icon={<FontAwesomeIcon icon={icon({ name: 'xmark', style: 'solid' })} style={{
        fontSize: 10,
        display: 'block',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: isRemoveHovered ? 'white' : undefined,
      }} />}
      style={{
        position: 'absolute',
        top: 2,
        ...(canRemoveFromRow ? { left: 2 } : { right: 2 }),
        zIndex: 4,
        width: 18,
        height: 18,
        minWidth: 18,
        padding: 0,
        backgroundColor: isRemoveHovered ? antdFormError : undefined,
        borderColor: isRemoveHovered ? 'white' : undefined,
      }}
    />}
    {/* Bande d'ajout de ligne, dans le bas de la colonne : juste en dessous,
        la bande d'ajout de container prend toute la largeur. Seul le « + »
        capte la souris : la bande recouvre le bas de la box, soit presque
        toute une box texte d'une ligne, qu'on ne pouvait plus cliquer sans
        créer une ligne. */}
    {showRowAdder && <div
      ref={rowAdderRef}
      data-outside-ignore='true'
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 14,
        zIndex: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        backgroundColor: isRowAdderHovered
          ? 'rgba(64, 150, 255, 0.16)'
          : 'rgba(64, 150, 255, 0.07)',
        transition: 'background-color 150ms ease',
        // Trou sur la poignée du bas de la box sélectionnée (cf. hook).
        clipPath: rowAdderClearance.clipPath,
      }}
    >
      <Button
        shape='circle'
        tabIndex={-1}
        title='Ajouter une ligne en dessous'
        onMouseEnter={() => setIsRowAdderHovered(true)}
        onMouseLeave={() => setIsRowAdderHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          addRowToColumn({ columnId: id });
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        style={{
          pointerEvents: 'auto',
          transform: `translateX(${rowAdderClearance.plusShift + (cellAddersSideBySide ? SIDE_BY_SIDE_OFFSET : 0)}px)`,
          width: 20,
          height: 20,
          minWidth: 20,
          minHeight: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
        icon={<FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} style={{ fontSize: 10, display: 'block' }} />}
      />
    </div>}
    {cellAdderAnchor && <AdderMenu
      columnId={id}
      closeMenu={closeCellAdder}
      anchorRef={cellAdderAnchor}
      fromViewer={fromViewer}
      onPick={handleCellPick}
    />}
    {!fromPdf && <CustomDragLayer id={id} drawMode={drawMode} zoom={zoomLevel} landscape={landscape} />}
  </div>
})

export default ColumnWrapper
